/**
 * ZHCB Design Tokens（TypeScript v3 — "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 與 globals.css 中的 CSS variables 一一對應。
 * 用於 inline style、type-safe 顏色引用、組件層常量。
 *
 * 色彩方向：農業驗證系統 → 信任 + 自然 → OKLCH 深森綠為主品牌色。
 *   主品牌：forest（OKLCH 0.43 0.12 145 系列）深森綠
 *   次強調：amber（暖琥珀，用於 warning / 次要高亮）
 *   中性：stone（帶暖偏的米灰，比純 slate 更典雅）
 *
 * 字體：
 *   display → Onest（現代幾何，標題品牌）
 *   body    → IBM Plex Sans（技術感正文，配 Noto Sans TC）
 *   mono    → IBM Plex Mono（tabular numerals，案件編號）
 *
 * 使用慣例：
 *   - 樣式優先用 className + CSS variable（var(--color-foo)）
 *   - 動態場景或必要的 inline style 使用本檔案的常量
 */

/* ════════════════════════════════════════════════════════
 * Color tokens
 * ════════════════════════════════════════════════════════ */

export const color = {
  /* ── Stone（暖偏中性，主面板底色） ── */
  stone: {
    50: '#fafaf9',
    100: '#f5f5f4',
    150: '#ededed',
    200: '#e7e5e4',
    300: '#d6d3d1',
    400: '#a8a29e',
    500: '#78716c',
    600: '#57534e',
    700: '#44403c',
    800: '#292524',
    900: '#1c1917',
    950: '#0c0a09',
  },

  /* ── Forest（品牌主色 — OKLCH 0.43 0.12 145 系列） ── */
  forest: {
    50: '#f0faf4',
    100: '#dcf4e7',
    200: '#bbe8cf',
    300: '#86d5a8',
    400: '#4dba7a',
    500: '#27a05a',
    600: '#1a8549' /* oklch(0.50 0.12 145) — 主要互動色 */,
    700: '#156939' /* oklch(0.43 0.12 145) — 按下/深色 */,
    800: '#114f2c',
    900: '#0d3820',
    950: '#071d11',
  },

  /* ── Amber（次強調，警告/對比） ── */
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  /* ── Emerald（成功狀態） ── */
  emerald: {
    50: '#ecfdf5',
    100: '#d1fae5',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
  },

  /* ── Rose（危險/錯誤） ── */
  rose: {
    50: '#fff1f2',
    100: '#ffe4e6',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',
  },

  /* ── Sky（信息/鏈接） ── */
  sky: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
  },

  /* ── Teal（次要強調） ── */
  teal: { 50: '#f0fdfa', 500: '#14b8a6', 600: '#0d9488' },

  /* ──────────────────────────────────────────────────────
   * 向後相容色板：v3 token 重構期間部分舊組件（AdminWorkbench /
   * StatusPill / dashboard 等）仍引用 indigo / violet / slate；
   * 補上完整 0-950 等級避免 runtime crash。後續完成重構時可移除。
   * ────────────────────────────────────────────────────── */

  /* ── Indigo（兼容 v2 主品牌色） ── */
  indigo: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
    950: '#1e1b4b',
  },

  /* ── Violet（兼容 v2 強調色） ── */
  violet: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
  },

  /* ── Slate（兼容 v2 中性色，與 stone 同義） ── */
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    150: '#ebeff5',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  /* ── 語義 alias ── */
  surface: {
    canvas: '#f9f9f8' /* 帶暖偏的頁面底色，比純 #f8fafc 更優雅 */,
    base: '#ffffff',
    raised: '#ffffff',
    overlay: '#ffffff',
    sunken: '#f5f5f4' /* stone-100 */,
    muted: '#fafaf9' /* stone-50 */,
    inverse: '#1c1917' /* stone-900 */,
  },

  border: {
    subtle: '#f0f0ef' /* 比 stone-100 稍淡，只分層不干擾視線 */,
    default: '#e7e5e4' /* stone-200 */,
    strong: '#d6d3d1' /* stone-300 */,
    inverse: 'rgba(255, 255, 255, 0.08)',
  },

  text: {
    primary: '#1c1917' /* stone-900 — 暖黑，比純黑更典雅 */,
    secondary: '#57534e' /* stone-600 */,
    tertiary: '#78716c' /* stone-500 */,
    muted: '#a8a29e' /* stone-400 */,
    disabled: '#d6d3d1' /* stone-300 */,
    inverse: '#ffffff',
    link: '#1a8549' /* forest-600 */,
    linkHover: '#156939' /* forest-700 */,
  },

  brand: {
    primary: '#1a8549' /* forest-600 */,
    primaryHover: '#156939' /* forest-700 */,
    primaryActive: '#114f2c' /* forest-800 */,
    primarySoft: '#f0faf4' /* forest-50 */,
    primarySoftHover: '#dcf4e7' /* forest-100 */,
    onPrimary: '#ffffff',
  },

  state: {
    success: '#059669' /* emerald-600 */,
    successSoft: '#ecfdf5',
    warning: '#d97706' /* amber-600 */,
    warningSoft: '#fffbeb',
    danger: '#e11d48' /* rose-600 */,
    dangerSoft: '#fff1f2',
    info: '#0284c7' /* sky-600 */,
    infoSoft: '#f0f9ff',
  },

  /* 深色側邊欄 — 基底比純黑稍暖（stone 傾向） */
  sidebar: {
    bg: '#111110' /* 暖感深黑，比 #0b0f1c 更雅 */,
    bgElevated: '#1c1917',
    border: 'rgba(255, 255, 255, 0.06)',
    text: '#a8a29e' /* stone-400 */,
    textActive: '#ffffff',
    textMuted: '#78716c' /* stone-500 */,
    hoverBg: 'rgba(255, 255, 255, 0.05)',
    activeBg: 'rgba(26, 133, 73, 0.15)' /* forest-600 @ 15% */,
    activeBar: '#27a05a' /* forest-500 */,
  },
} as const;

/* ════════════════════════════════════════════════════════
 * Typography — Onest + IBM Plex Sans + IBM Plex Mono
 * ════════════════════════════════════════════════════════ */

export const font = {
  /** Onest：幾何現代感，用於標題、品牌文字、頁面大標 */
  display:
    "'Onest', 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Noto Sans TC', 'PingFang TC', sans-serif",
  /** IBM Plex Sans：高可讀性技術感正文，配繁中 fallback */
  body: "'IBM Plex Sans', 'Noto Sans TC', 'PingFang TC', -apple-system, BlinkMacSystemFont, sans-serif",
  /** IBM Plex Mono：tabular numerals，案件編號 AUD-XXXX / KPI 數字 */
  mono: "'IBM Plex Mono', 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
  /** 繁中專用 fallback */
  zh: "'Noto Sans TC', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
} as const;

export const text = {
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 15,
  xl: 18,
  '2xl': 22,
  '3xl': 28,
  '4xl': 36,
  '5xl': 48,
} as const;

export const leading = {
  tight: 1.2,
  snug: 1.4,
  normal: 1.55,
  relaxed: 1.7,
} as const;

export const tracking = {
  tight: '-0.02em',
  normal: '0',
  wide: '0.02em',
  wider: '0.06em',
  widest: '0.12em',
} as const;

export const weight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

/* ════════════════════════════════════════════════════════
 * Spacing
 * ════════════════════════════════════════════════════════ */

export const space = {
  0: 0,
  px: 1,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
} as const;

/* ════════════════════════════════════════════════════════
 * Radius
 * ════════════════════════════════════════════════════════ */

export const radius = {
  none: 0,
  xs: 3,
  sm: 5 /* 從 4 升至 5：稍微圓潤，更現代 */,
  md: 8 /* 從 6 升至 8：卡片/容器標準值 */,
  lg: 10,
  xl: 14,
  '2xl': 18,
  full: 9999,
} as const;

/* ════════════════════════════════════════════════════════
 * Shadow — 克制，多層，暖偏
 * ════════════════════════════════════════════════════════ */

export const shadow = {
  xs: '0 1px 2px rgba(28, 25, 23, 0.04)',
  sm: '0 1px 2px rgba(28, 25, 23, 0.04), 0 1px 1px rgba(28, 25, 23, 0.02)',
  md: '0 2px 4px rgba(28, 25, 23, 0.04), 0 4px 8px rgba(28, 25, 23, 0.04)',
  lg: '0 4px 8px rgba(28, 25, 23, 0.04), 0 12px 24px rgba(28, 25, 23, 0.06)',
  xl: '0 8px 16px rgba(28, 25, 23, 0.05), 0 24px 48px rgba(28, 25, 23, 0.10)',
  /** focus ring：brand green @ 20% */
  focusRing: '0 0 0 3px rgba(26, 133, 73, 0.18)',
  inset: 'inset 0 1px 2px rgba(28, 25, 23, 0.04)',
} as const;

/* ════════════════════════════════════════════════════════
 * Motion
 * ════════════════════════════════════════════════════════ */

export const motion = {
  fast: '120ms',
  base: '200ms',
  slow: '320ms',
  slower: '480ms',
} as const;

export const ease = {
  out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/* ════════════════════════════════════════════════════════
 * Breakpoints — 與 antd Grid (Col xs/sm/md/lg/xl/xxl) 對齊
 * ════════════════════════════════════════════════════════
 * antd 預設斷點：xs<576, sm≥576, md≥768, lg≥992, xl≥1200, xxl≥1600
 * 此處用於：BaseLayout 自動 collapse、Modal/Drawer 寬度切換、Table 列響應式。
 * iPad 對齊：
 *   - 直立（768×1024）  → md，不到 lg → 視為 isPadPortrait
 *   - 橫向（1024×768/1366×1024） → lg/xl → 視為 isPadLandscape / isDesktop
 */
export const breakpoint = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

/**
 * 直接可用於 inline style/CSS-in-JS 的 media query 字串。
 * 注意：antd Grid.useBreakpoint() 是首選；本常量僅用於 inline style 不便處。
 */
export const mediaQuery = {
  /** 直到（含）對應斷點 — 適合 mobile-first 反向覆寫 */
  maxSm: `@media (max-width: ${breakpoint.sm - 1}px)`,
  maxMd: `@media (max-width: ${breakpoint.md - 1}px)`,
  maxLg: `@media (max-width: ${breakpoint.lg - 1}px)`,
  /** 從對應斷點起 — 桌面增強 */
  minMd: `@media (min-width: ${breakpoint.md}px)`,
  minLg: `@media (min-width: ${breakpoint.lg}px)`,
  minXl: `@media (min-width: ${breakpoint.xl}px)`,
} as const;

/* ════════════════════════════════════════════════════════
 * 角色品牌色（用於 Logo / 徽章 / 漸變等）
 * ════════════════════════════════════════════════════════ */

export const roleAccent = {
  admin: {
    primary: color.forest[600],
    secondary: color.forest[400],
    gradient: `linear-gradient(135deg, ${color.forest[700]} 0%, ${color.forest[500]} 100%)`,
  },
  auditor: {
    primary: color.sky[600],
    secondary: color.teal[600],
    gradient: `linear-gradient(135deg, ${color.sky[700]} 0%, ${color.teal[500]} 100%)`,
  },
  counselor: {
    primary: color.amber[600],
    secondary: color.amber[400],
    gradient: `linear-gradient(135deg, ${color.amber[700]} 0%, ${color.amber[400]} 100%)`,
  },
} as const;

/* ════════════════════════════════════════════════════════
 * 默認導出（方便引用）
 * ════════════════════════════════════════════════════════ */

export const tokens = {
  color,
  font,
  text,
  leading,
  tracking,
  weight,
  space,
  radius,
  shadow,
  motion,
  ease,
  roleAccent,
  breakpoint,
  mediaQuery,
} as const;

export default tokens;
